<template>
  <list-page-shell>
    <template #actions>
      <el-button type="primary" size="small" icon="el-icon-plus" @click="openQuoteNew">{{ $t('quoteListPage.create') }}</el-button>
    </template>

    <template #filters>
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item :label="$t('quoteListPage.quoteNoLabel')">
          <el-input
            v-model="filters.quoteNoLike"
            clearable
            :placeholder="$t('quoteListPage.quoteNoPh')"
            style="width:200px;"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item :label="$t('quoteListPage.statusFilter')">
          <el-select v-model="filters.quoteStatusCode" clearable :placeholder="$t('common.pleaseSelect')" style="width:160px;">
            <el-option v-for="o in quoteStatusOptions" :key="o.value" :value="o.value" :label="o.label" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">{{ $t('common.search') }}</el-button>
          <el-button icon="el-icon-refresh" :disabled="loading" @click="handleReset">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>
    </template>

    <el-table :data="list" border stripe v-loading="loading" style="width:100%;">
      <el-table-column prop="quoteNo" :label="$t('grid.number')" width="132" show-overflow-tooltip />
      <el-table-column prop="leadNo" :label="$t('grid.leadNo')" width="120" show-overflow-tooltip />
      <el-table-column prop="leadDisplayName" :label="$t('grid.leadDisplayName')" min-width="140" show-overflow-tooltip />
      <el-table-column :label="$t('quoteListPage.customerSnapshot')" min-width="120" show-overflow-tooltip>
        <template slot-scope="s">{{ quoteCustomerDisplay(s.row) }}</template>
      </el-table-column>
      <el-table-column :label="$t('grid.owner')" width="110" show-overflow-tooltip>
        <template slot-scope="s">{{ s.row.salesOwnerDisplayName || '—' }}</template>
      </el-table-column>
      <el-table-column :label="$t('grid.totalUsd')" min-width="112" align="right">
        <template slot-scope="s">{{ formatUsd(s.row.totalAmount) }}</template>
      </el-table-column>
      <el-table-column :label="$t('quoteListPage.statusFilter')" min-width="138" show-overflow-tooltip>
        <template slot-scope="s">{{ labelQuotePrimary(s.row) }}</template>
      </el-table-column>
      <el-table-column :label="$t('quoteFormPage.validUntil')" width="118" show-overflow-tooltip>
        <template slot-scope="s">{{ s.row.validUntil || '—' }}</template>
      </el-table-column>
      <el-table-column :label="$t('grid.createdAt')" width="200" show-overflow-tooltip>
        <template slot-scope="s">{{ formatDisplayDateTime(s.row.createdAt) || '—' }}</template>
      </el-table-column>
      <el-table-column :label="$t('common.actions')" width="360" fixed="right">
        <template slot-scope="s">
          <el-button
            v-if="flags(s.row).editable"
            type="text"
            size="small"
            @click="openQuoteEdit(s.row.id)"
          >{{ $t('common.edit') }}</el-button>
          <el-button
            v-else
            type="text"
            size="small"
            @click="openQuoteEdit(s.row.id)"
          >{{ $t('quoteListPage.view') }}</el-button>
          <el-button
            v-if="flags(s.row).canSubmit"
            type="text"
            size="small"
            @click="submitApproval(s.row)"
          >{{ $t('quoteListPage.submitInternal') }}</el-button>
          <el-button
            v-if="flags(s.row).canApprove"
            type="text"
            size="small"
            v-permission="['OFFICE','ADMIN']"
            @click="approve(s.row)"
          >{{ $t('quoteListPage.approve') }}</el-button>
          <el-button
            v-if="flags(s.row).canApprove"
            type="text"
            size="small"
            v-permission="['OFFICE','ADMIN']"
            @click="reject(s.row)"
          >{{ $t('quoteListPage.reject') }}</el-button>
          <el-button
            v-if="flags(s.row).canSend"
            type="text"
            size="small"
            @click="sendLink(s.row)"
          >{{ $t('quoteListPage.sendLink') }}</el-button>
          <el-button
            v-if="quoteRowCustomerApproved(s.row) && !s.row.activeContractId"
            type="text"
            size="small"
            @click="onCreateContractFromQuote({ quoteId: s.row.id })"
          >{{ $t('quoteListPage.createContract') }}</el-button>
          <el-button
            v-else-if="quoteRowCustomerApproved(s.row) && s.row.activeContractId"
            type="text"
            size="small"
            @click="openContractDetail(s.row.activeContractId)"
          >{{ $t('quoteListPage.viewContractDetail') }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="list-pager">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :total="total"
        :page-sizes="[10, 20, 50]"
        :page-size="pageSize"
        :current-page="pageNo"
        @size-change="onSizeChange"
        @current-change="p => { pageNo = p; load() }"
      />
    </div>

    <RecordFormDialog :visible.sync="quoteFormVisible" :title="quoteFormTitle">
      <QuoteForm
        v-if="quoteFormVisible"
        ref="quoteFormRef"
        :key="quoteFormKey"
        embedded
        :record-id="quoteFormRecordId"
        :initial-lead-id="quoteFormLeadId"
        @saved="onQuoteFormSaved"
        @cancel="quoteFormVisible = false"
        @saving="v => (quoteFormSaving = v)"
        @embedded-footer="onQuoteEmbeddedFooter"
        @create-contract-from-quote="onCreateContractFromQuote"
      />
      <template slot="footer">
        <el-button @click="quoteFormVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button
          v-if="quoteFormViewContractId"
          type="warning"
          plain
          @click="goQuoteLinkedContractDetail"
        >{{ $t('quoteListPage.viewContractDetail') }}</el-button>
        <el-button
          v-if="quoteFormShowCreateContract"
          type="warning"
          plain
          @click="openContractFromQuoteInQuoteDialog"
        >{{ $t('quoteFormPage.createContractFromQuote') }}</el-button>
        <template v-if="!quoteFormLocked">
          <el-button
            type="success"
            plain
            :loading="quoteFormSaving"
            @click="submitQuoteApprovalDialog"
          >{{ $t('quoteFormPage.saveSubmitApproval') }}</el-button>
          <el-button
            type="primary"
            :loading="quoteFormSaving"
            @click="submitQuoteSaveDialog"
          >{{ $t('quoteFormPage.saveDraft') }}</el-button>
        </template>
        <el-button
          v-if="quoteFormShowDeleteDraft"
          type="danger"
          plain
          :loading="quoteFormSaving"
          @click="deleteQuoteDraftDialog"
        >{{ $t('quoteFormPage.deleteDraft') }}</el-button>
      </template>
    </RecordFormDialog>

    <RecordFormDialog :visible.sync="contractFromQuoteVisible" :title="$t('contractFromQuotePage.title')">
      <ContractFromQuote
        v-if="contractFromQuoteVisible"
        :key="contractFromQuoteKey"
        ref="contractFromQuoteRef"
        embedded
        :preset-quote-id="contractFromQuoteQuoteId"
        @saved="onContractFromQuoteSaved"
        @saving="v => (contractFromQuoteSaving = v)"
      />
      <template slot="footer">
        <el-button @click="contractFromQuoteVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          :loading="contractFromQuoteSaving"
          @click="submitContractFromQuoteDialog"
        >{{ $t('contractFromQuotePage.submitOpenDetail') }}</el-button>
      </template>
    </RecordFormDialog>
  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import RecordFormDialog from '@/components/RecordFormDialog.vue'
import QuoteForm from '@/views/quote/QuoteForm.vue'
import ContractFromQuote from '@/views/contract/ContractFromQuote.vue'
import { getQuotePage, approveInternal, rejectInternal, sendQuoteLink, submitQuote } from '@/api/quote'
import { enumSelectOptions, labelQuotePrimary, quoteUiFlags } from '@/utils/crmLabels'
import { formatUsd } from '@/utils/formatUsd'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'
import { contractDetailLocation } from '@/utils/contractDetailNav'

export default {
  name: 'QuoteList',
  components: { ListPageShell, RecordFormDialog, QuoteForm, ContractFromQuote },
  data() {
    return {
      quoteFormVisible: false,
      quoteFormSaving: false,
      quoteFormLocked: false,
      quoteFormShowDeleteDraft: false,
      quoteFormShowCreateContract: false,
      quoteFormViewContractId: null,
      quoteFormRecordId: null,
      quoteFormLeadId: null,
      quoteFormKey: 0,
      contractFromQuoteVisible: false,
      contractFromQuoteSaving: false,
      contractFromQuoteQuoteId: null,
      contractFromQuoteKey: 0,
      list: [],
      total: 0,
      pageNo: 1,
      pageSize: 10,
      loading: false,
      filters: { quoteNoLike: '', quoteStatusCode: null }
    }
  },
  computed: {
    quoteFormTitle() {
      return this.quoteFormRecordId
        ? this.$t('quoteFormPage.editTitle')
        : this.$t('quoteFormPage.newTitle')
    },
    quoteStatusOptions() {
      void this.$locale
      return enumSelectOptions(this.$t, 'quoteStatus', [1, 2, 3, 4, 7, 5, 6])
    }
  },
  watch: {
    '$route.query': {
      handler(q) {
        this.applyQuoteFormQuery(q || {})
      }
    }
  },
  created() {
    this.load()
    this.applyQuoteFormQuery(this.$route.query || {})
  },
  methods: {
    applyQuoteFormQuery(q) {
      if (!q) return
      if (q.openQuoteEdit != null && q.openQuoteEdit !== '') {
        this.resetQuoteFormUi()
        this.quoteFormRecordId = Number(q.openQuoteEdit)
        this.quoteFormLeadId = null
        this.quoteFormKey += 1
        this.quoteFormVisible = true
        this.$router.replace({ path: '/quote/list', query: {} }).catch(() => {})
        return
      }
      if (String(q.openQuoteForm) !== '1') return
      this.resetQuoteFormUi()
      this.quoteFormRecordId = null
      const lid = q.leadId
      this.quoteFormLeadId = lid !== undefined && lid !== '' ? Number(lid) : null
      this.quoteFormKey += 1
      this.quoteFormVisible = true
      this.$router.replace({ path: '/quote/list', query: {} }).catch(() => {})
    },
    openQuoteNew() {
      this.resetQuoteFormUi()
      this.quoteFormRecordId = null
      this.quoteFormLeadId = null
      this.quoteFormKey += 1
      this.quoteFormVisible = true
    },
    openQuoteEdit(id) {
      this.resetQuoteFormUi()
      this.quoteFormRecordId = id
      this.quoteFormLeadId = null
      this.quoteFormKey += 1
      this.quoteFormVisible = true
    },
    resetQuoteFormUi() {
      this.quoteFormLocked = false
      this.quoteFormShowDeleteDraft = false
      this.quoteFormShowCreateContract = false
      this.quoteFormViewContractId = null
    },
    onQuoteEmbeddedFooter(p) {
      if (!p) return
      this.quoteFormLocked = !!p.formLocked
      this.quoteFormShowDeleteDraft = !!p.showDeleteDraft
      this.quoteFormShowCreateContract = !!p.showCreateContractFromQuote
      const vid = p.viewContractId
      this.quoteFormViewContractId =
        vid != null && vid !== '' && Number.isFinite(Number(vid)) ? Number(vid) : null
    },
    goQuoteLinkedContractDetail() {
      const id = this.quoteFormViewContractId
      if (id == null) return
      this.quoteFormVisible = false
      this.$router.push(contractDetailLocation(id, this.$route.fullPath))
    },
    openContractDetail(contractId) {
      this.$router.push(contractDetailLocation(contractId, this.$route.fullPath))
    },
    quoteRowCustomerApproved(row) {
      return Number(row && row.quoteStatusCode) === 5
    },
    openContractFromQuoteInQuoteDialog() {
      if (this.$refs.quoteFormRef) this.$refs.quoteFormRef.openContractFromQuote()
    },
    submitQuoteSaveDialog() {
      if (this.$refs.quoteFormRef) this.$refs.quoteFormRef.save()
    },
    submitQuoteApprovalDialog() {
      if (this.$refs.quoteFormRef) this.$refs.quoteFormRef.submitForApproval()
    },
    deleteQuoteDraftDialog() {
      if (this.$refs.quoteFormRef) this.$refs.quoteFormRef.deleteDraft()
    },
    onCreateContractFromQuote({ quoteId }) {
      const qid = quoteId != null && quoteId !== '' ? Number(quoteId) : null
      if (!qid) return
      this.quoteFormVisible = false
      this.contractFromQuoteQuoteId = qid
      this.contractFromQuoteKey += 1
      this.$nextTick(() => {
        this.contractFromQuoteVisible = true
      })
    },
    submitContractFromQuoteDialog() {
      if (this.$refs.contractFromQuoteRef) this.$refs.contractFromQuoteRef.submit()
    },
    onContractFromQuoteSaved({ id }) {
      this.contractFromQuoteVisible = false
      this.load()
      if (id != null && id !== '') this.$router.push(contractDetailLocation(id, this.$route.fullPath))
    },
    onQuoteFormSaved() {
      this.quoteFormVisible = false
      this.load()
    },
    formatUsd,
    formatDisplayDateTime,
    quoteCustomerDisplay(row) {
      if (!row) return '—'
      const parts = [row.customerFirstName, row.customerLastName].filter(x => x != null && String(x).trim() !== '')
      const name = parts.map(x => String(x).trim()).join(' ').trim()
      return name || '—'
    },
    labelQuotePrimary,
    flags: quoteUiFlags,
    onSizeChange(size) {
      this.pageSize = size
      this.pageNo = 1
      this.load()
    },
    handleSearch() {
      this.pageNo = 1
      this.load()
    },
    handleReset() {
      this.filters = { quoteNoLike: '', quoteStatusCode: null }
      this.pageNo = 1
      this.load()
    },
    buildQueryParams() {
      const params = { pageNo: this.pageNo, pageSize: this.pageSize }
      const q = (this.filters.quoteNoLike || '').trim()
      if (q) params.quoteNoLike = q
      if (this.filters.quoteStatusCode != null && this.filters.quoteStatusCode !== '') {
        params.quoteStatusCode = this.filters.quoteStatusCode
      }
      return params
    },
    async load() {
      this.loading = true
      try {
        const r = await getQuotePage(this.buildQueryParams())
        const d = r.data || r
        this.list = d.records || []
        this.total = d.total || 0
      } finally {
        this.loading = false
      }
    },
    async submitApproval(row) {
      try {
        await submitQuote(row.id)
        this.$message.success(this.$t('quoteListPage.submitted'))
        this.load()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('quoteListPage.failed'))
      }
    },
    async approve(row) {
      try {
        await approveInternal(row.id, '')
        this.$message.success(this.$t('quoteListPage.approved'))
        this.load()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('quoteListPage.failed'))
      }
    },
    async reject(row) {
      try {
        await rejectInternal(row.id, this.$t('quoteListPage.rejectReason'))
        this.$message.success(this.$t('quoteListPage.rejected'))
        this.load()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('quoteListPage.failed'))
      }
    },
    async sendLink(row) {
      const displayMode = await this.chooseSendDisplayMode()
      if (!displayMode) return
      const fail = (msg, alertType) => {
        const text = msg || this.$t('quoteListPage.sendQuoteFailed')
        this.$alert(text, this.$t('quoteListPage.sendQuoteFailTitle'), {
          type: alertType || 'error',
          confirmButtonText: this.$t('common.confirm')
        })
      }
      try {
        const r = await sendQuoteLink(row.id, displayMode)
        if (!r || r.code !== 200) {
          fail((r && r.msg) || null, 'warning')
          return
        }
        const d = r.data || {}
        this.$message.success(this.$t('quoteListPage.linkEmailed', { email: d.sentToEmail || '—' }))
        this.load()
      } catch (e) {
        fail((e && e.message) || null, 'error')
      }
    },
    async chooseSendDisplayMode() {
      try {
        await this.$confirm(
          this.$t('quoteListPage.sendModeMessage'),
          this.$t('quoteListPage.sendModeTitle'),
          {
            distinguishCancelAndClose: true,
            confirmButtonText: this.$t('quoteListPage.sendModeDetails'),
            cancelButtonText: this.$t('quoteListPage.sendModeSummary'),
            type: 'info'
          }
        )
        return 'DETAILS'
      } catch (action) {
        if (action === 'cancel') return 'SUMMARY'
        return null
      }
    }
  }
}
</script>

<style scoped>
.list-pager {
  margin-top: 16px;
  text-align: right;
}
</style>
